import { cn } from '@/lib/utils'
import type { PrintifyProductOption, PrintifyProductVariant } from '../types'

interface VariantPickerProps {
  options: PrintifyProductOption[]
  variants: PrintifyProductVariant[]
  selectedOptions: Record<number, number> // optionIndex → valueId
  onOptionChange: (optionIndex: number, valueId: number) => void
}

/** Returns whether any enabled variant exists for this value, regardless of other selections. */
function hasAnyEnabledVariant(
  variants: PrintifyProductVariant[],
  optionIndex: number,
  valueId: number
): boolean {
  return variants.some((v) => v.is_enabled && v.options[optionIndex] === valueId)
}

/** Returns whether this value is compatible with the current other selections. */
function isCompatibleWithSelection(
  variants: PrintifyProductVariant[],
  optionIndex: number,
  valueId: number,
  selectedOptions: Record<number, number>
): boolean {
  return variants.some((v) => {
    if (!v.is_enabled) return false
    if (v.options[optionIndex] !== valueId) return false
    return Object.entries(selectedOptions).every(([idxStr, selId]) => {
      const idx = Number(idxStr)
      if (idx === optionIndex) return true
      return v.options[idx] === selId
    })
  })
}

export function VariantPicker({
  options,
  variants,
  selectedOptions,
  onOptionChange,
}: VariantPickerProps) {
  return (
    <div className="space-y-4">
      {options.map((option, optionIndex) => {
        const isColor = option.type === 'color'
        return (
          <div key={optionIndex}>
            <p className="mb-2 text-sm font-medium">
              {option.name}
              {selectedOptions[optionIndex] !== undefined && (
                <span className="ml-2 font-normal text-muted-foreground">
                  {option.values.find((v) => v.id === selectedOptions[optionIndex])?.title}
                </span>
              )}
            </p>
            <div className="flex flex-wrap gap-2">
              {option.values
                .filter((value) => hasAnyEnabledVariant(variants, optionIndex, value.id))
                .map((value) => {
                const isSelected = selectedOptions[optionIndex] === value.id
                const compatible = isCompatibleWithSelection(
                  variants,
                  optionIndex,
                  value.id,
                  selectedOptions
                )
                const incompatibleTitle = compatible
                  ? value.title
                  : `${value.title} — not available with current selection; other options will adjust`

                if (isColor && value.colors?.length) {
                  return (
                    <button
                      key={value.id}
                      type="button"
                      title={incompatibleTitle}
                      onClick={() => onOptionChange(optionIndex, value.id)}
                      className={cn(
                        'relative h-8 w-8 rounded-full ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                        isSelected && 'ring-2 ring-foreground ring-offset-2',
                        !compatible && !isSelected && 'opacity-50'
                      )}
                      style={{ backgroundColor: value.colors[0] }}
                    >
                      {!compatible && !isSelected && (
                        <span
                          aria-hidden
                          className="pointer-events-none absolute inset-0 flex items-center justify-center"
                        >
                          <span className="block h-[2px] w-[140%] rotate-45 bg-foreground/70" />
                        </span>
                      )}
                    </button>
                  )
                }

                return (
                  <button
                    key={value.id}
                    type="button"
                    title={incompatibleTitle}
                    onClick={() => onOptionChange(optionIndex, value.id)}
                    className={cn(
                      'rounded border px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      isSelected
                        ? 'border-foreground bg-foreground text-background'
                        : 'border-border bg-background text-foreground hover:bg-accent',
                      !compatible && !isSelected && 'text-muted-foreground line-through opacity-60'
                    )}
                  >
                    {value.title}
                  </button>
                )
                })}
            </div>
          </div>
        )
      })}
    </div>
  )
}
